import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, CheckCircle2, XCircle, AlertCircle, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  sent: { color: 'text-green-700 bg-green-50', icon: CheckCircle2 },
  failed: { color: 'text-red-700 bg-red-50', icon: XCircle },
  skipped: { color: 'text-amber-700 bg-amber-50', icon: AlertCircle },
};

const LogsTab = ({ templates }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [templateFilter, setTemplateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (templateFilter) params.set('template_key', templateFilter);
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get(`${API}/admin/email-logs?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [templateFilter, statusFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-4" data-testid="logs-tab">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={templateFilter}
          onChange={(e) => setTemplateFilter(e.target.value)}
          className="border rounded-md px-2 py-1.5 text-sm"
          data-testid="logs-template-filter"
        >
          <option value="">All templates</option>
          {templates.map((t) => (
            <option key={t.key} value={t.key}>{t.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border rounded-md px-2 py-1.5 text-sm"
          data-testid="logs-status-filter"
        >
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        <Button variant="ghost" size="sm" onClick={fetchLogs} data-testid="logs-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
        <span className="text-xs text-gray-500 ml-auto">{total} total in last 30 days</span>
      </div>

      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-green-600" /></div>
      ) : logs.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500 text-sm">No emails sent yet.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="text-left p-3">When</th>
                    <th className="text-left p-3">Template</th>
                    <th className="text-left p-3">Recipient</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const styles = STATUS_STYLES[log.status] || { color: 'text-gray-600 bg-gray-100', icon: AlertCircle };
                    const Icon = styles.icon;
                    return (
                      <tr key={log._id} className="border-t" data-testid={`log-row-${log._id}`}>
                        <td className="p-3 text-xs text-gray-500 whitespace-nowrap">
                          {new Date(log.sent_at).toLocaleString()}
                        </td>
                        <td className="p-3 font-mono text-xs">{log.template_key}</td>
                        <td className="p-3">{log.recipient}</td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${styles.color}`}>
                            <Icon className="h-3 w-3" />
                            {log.status}
                          </span>
                          {log.error && <p className="text-[10px] text-red-600 mt-1">{log.error}</p>}
                        </td>
                        <td className="p-3 text-xs text-gray-500">
                          {log.manual ? (
                            <span>Manual {log.bulk_batch_id ? `(bulk)` : ''} · {log.sent_by || 'admin'}</span>
                          ) : (
                            <span className="text-blue-700">Automation</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LogsTab;
